import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AiModelPrice,
  AiModelPrices,
  AiUsageReport,
  CreateAiModelPriceInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['admin', 'ai-usage'] as const;
const PRICES_KEY = ['admin', 'ai-model-prices'] as const;

export function useAiUsageReport(period: { from: string; to: string }) {
  return useQuery<AiUsageReport, ApiClientError>({
    queryKey: [...KEY, period],
    queryFn: () => apiClient.get<AiUsageReport>('/admin/ai-usage', { query: period }),
  });
}

export function useAiModelPrices() {
  return useQuery<AiModelPrices, ApiClientError>({
    queryKey: PRICES_KEY,
    queryFn: () => apiClient.get<AiModelPrices>('/admin/ai-model-prices'),
  });
}

/** Prices change every cost figure - refresh the report too. */
function useRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: PRICES_KEY });
    void qc.invalidateQueries({ queryKey: KEY });
  };
}

export function useAddAiModelPrice() {
  const refresh = useRefresh();
  return useMutation<AiModelPrice, ApiClientError, CreateAiModelPriceInput>({
    mutationFn: (input) => apiClient.post<AiModelPrice>('/admin/ai-model-prices', input),
    onSuccess: refresh,
  });
}

export function useDeleteAiModelPrice() {
  const refresh = useRefresh();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => apiClient.delete<void>(`/admin/ai-model-prices/${id}`),
    onSuccess: refresh,
  });
}

export function useSetUsdToInr() {
  const refresh = useRefresh();
  return useMutation<{ usdToInr: number }, ApiClientError, number>({
    mutationFn: (usdToInr) =>
      apiClient.put<{ usdToInr: number }>('/admin/ai-usage/usd-to-inr', { usdToInr }),
    onSuccess: refresh,
  });
}
